import React, { useState, useEffect, Suspense, lazy } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { roleHome } from './utils/roleHome';
import { BrandingProvider } from './context/BrandingContext';
import { PermissionsProvider } from './context/PermissionsContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import { GrowthEngineRoute } from './components/growth/kit';   // Growth AA
import ErrorBoundary from './components/ErrorBoundary';
import ModuleErrorBoundary from './components/ModuleErrorBoundary';
import LegalReacceptanceModal from './components/LegalReacceptanceModal';
import CookieConsent from './components/CookieConsent';
import MorningBriefingModal from './components/MorningBriefingModal';
import AskAnywherePalette from './components/AskAnywherePalette';
import { legalConsentAPI as legalAPI } from './utils/api';

// ── Auth pages — loaded eagerly (needed immediately) ─────────────────────────
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import LegalCertificatePage from './pages/LegalCertificatePage';

// ── All other pages — lazy loaded (only fetched when navigated to) ────────────
const ProfilePage               = lazy(() => import('./pages/ProfilePage'));
const ChangePasswordPage        = lazy(() => import('./pages/ChangePasswordPage'));
const AdminDashboard            = lazy(() => import('./pages/AdminDashboard'));
const AdminStaff                = lazy(() => import('./pages/AdminStaff'));
// Processing module — webpackPrefetch tells the browser to fetch sibling
// chunks in idle time after the main bundle loads. Without it every tab
// click triggers a fresh chunk fetch → blank-white Suspense fallback.
// Same pattern applied to HR earlier.
const AdminClients              = lazy(() => import(/* webpackPrefetch: true */ './pages/AdminClients'));
const AdminCases                = lazy(() => import(/* webpackPrefetch: true */ './pages/AdminCases'));
const AdminVisaPrograms         = lazy(() => import(/* webpackPrefetch: true */ './pages/AdminVisaPrograms'));
const ProcessingOverview        = lazy(() => import(/* webpackPrefetch: true */ './pages/processing/ProcessingOverview'));
const CasePipeline              = lazy(() => import(/* webpackPrefetch: true */ './pages/processing/CasePipeline'));
const Autopilot                 = lazy(() => import('./pages/Autopilot'));
const AskEuphoric               = lazy(() => import('./pages/AskEuphoric'));
const B2BPartners               = lazy(() => import('./pages/B2BPartners'));
const B2BRegisterClient         = lazy(() => import('./pages/B2BRegisterClient'));
const ClientPortal              = lazy(() => import('./pages/ClientPortal'));
const PartnerToday              = lazy(() => import('./pages/partner/PartnerToday'));
const PartnerClients            = lazy(() => import('./pages/partner/PartnerClients'));
const PartnerCaseDetail         = lazy(() => import('./pages/partner/PartnerCaseDetail'));
const ClientComplaints          = lazy(() => import('./pages/ClientComplaints'));
const AdminChecklistTemplates   = lazy(() => import(/* webpackPrefetch: true */ './pages/AdminChecklistTemplates'));
const CaseDetail                = lazy(() => import(/* webpackPrefetch: true */ './pages/CaseDetail'));
const UsersManagement           = lazy(() => import('./pages/UsersManagement'));
const SettingsPage              = lazy(() => import('./pages/SettingsPage'));
const LeadIntakeSettings        = lazy(() => import('./pages/LeadIntakeSettings'));
const RecycleBinPage            = lazy(() => import('./pages/RecycleBinPage'));
const LeadDeletionReportPage    = lazy(() => import('./pages/LeadGovernanceReports').then(m => ({ default: m.LeadDeletionReportPage })));
const LeadReassignmentReportPage = lazy(() => import('./pages/LeadGovernanceReports').then(m => ({ default: m.LeadReassignmentReportPage })));
const DiscountPolicyPage        = lazy(() => import('./pages/DiscountPolicyPages').then(m => ({ default: m.DiscountPolicyPage })));
const DiscountApprovalsPage     = lazy(() => import('./pages/DiscountPolicyPages').then(m => ({ default: m.DiscountApprovalsPage })));
const DiscountAuditPage         = lazy(() => import('./pages/DiscountPolicyPages').then(m => ({ default: m.DiscountAuditPage })));
const EmailLogs                 = lazy(() => import('./pages/EmailLogs'));
const EmailTemplates            = lazy(() => import('./pages/EmailTemplates'));
const OfficerDocuments          = lazy(() => import('./pages/OfficerDocuments'));
const CaseOfficerDashboard      = lazy(() => import('./pages/CaseOfficerDashboard'));
const OfficerComplaints         = lazy(() => import('./pages/OfficerComplaints'));
const AdminComplaints           = lazy(() => import('./pages/AdminComplaints'));
const DocumentLibrary           = lazy(() => import(/* webpackPrefetch: true */ './pages/DocumentLibrary'));
const EmailModule               = lazy(() => import('./pages/EmailModule'));
const AccountingDashboard       = lazy(() => import('./pages/AccountingDashboard'));
const SalesManagement           = lazy(() => import('./pages/SalesManagement'));
const ExpenseManagement         = lazy(() => import('./pages/ExpenseManagement'));
const CollectionsQueue          = lazy(() => import('./pages/accounting/CollectionsQueue'));
const FinancialReports          = lazy(() => import('./pages/FinancialReports'));
const ComplaintNaturesManagement = lazy(() => import('./pages/ComplaintNaturesManagement'));
const NotificationsPage         = lazy(() => import('./pages/NotificationsPage'));
const AdminClientDetail         = lazy(() => import(/* webpackPrefetch: true */ './pages/AdminClientDetail'));
const SalesDashboard            = lazy(() => import('./pages/sales/SalesDashboard'));
const SalesManagerDashboard     = lazy(() => import('./pages/sales/SalesManagerDashboard'));
const LeadsKanban               = lazy(() => import('./pages/sales/LeadsKanban'));
const LeadPoolPage              = lazy(() => import('./pages/sales/LeadPoolPage'));
// HR pages — webpackPrefetch tells the browser to fetch sibling chunks in
// idle time after the main bundle loads. Result: tab switches under HR feel
// instant (no blink) instead of triggering a fresh chunk download each time.
const HROverview                = lazy(() => import(/* webpackPrefetch: true */ './pages/hr/HROverview'));
const AttendanceLedger          = lazy(() => import(/* webpackPrefetch: true */ './pages/hr/AttendanceLedger'));
const PayrollMonthEnd           = lazy(() => import(/* webpackPrefetch: true */ './pages/hr/PayrollMonthEnd'));
const HRPolicySettings          = lazy(() => import(/* webpackPrefetch: true */ './pages/hr/HRPolicySettings'));
const Leaves                    = lazy(() => import(/* webpackPrefetch: true */ './pages/hr/Leaves'));
const JobApplications           = lazy(() => import(/* webpackPrefetch: true */ './pages/hr/JobApplications'));
const MyAttendance              = lazy(() => import(/* webpackPrefetch: true */ './pages/hr/MyAttendance'));
const LeadDetail                = lazy(() => import('./pages/sales/LeadDetail'));
const MarketingLeadsUpload      = lazy(() => import('./pages/marketing/MarketingLeadsUpload'));
const MarketingImportHistory    = lazy(() => import('./pages/marketing/MarketingImportHistory'));
const LeadPool                  = lazy(() => import('./pages/marketing/LeadPool'));
const MarketingDashboard        = lazy(() => import('./pages/marketing/MarketingDashboard'));
const LeadSources               = lazy(() => import('./pages/marketing/LeadSources'));
const AssignedLeadsReport        = lazy(() => import('./pages/marketing/AssignedLeadsReport'));
const MetaPerformance            = lazy(() => import('./pages/marketing/MetaPerformance'));
const Acquisition                = lazy(() => import('./pages/marketing/Acquisition'));
const TechnicalSeo               = lazy(() => import('./pages/marketing/TechnicalSeo'));   // Growth AA
const Keywords                   = lazy(() => import('./pages/marketing/Keywords'));      // Growth AB
const Competitors                = lazy(() => import('./pages/marketing/Competitors'));   // Growth AC
const Gaps                       = lazy(() => import('./pages/marketing/Gaps'));          // Growth AD
const Authority                  = lazy(() => import('./pages/marketing/Authority'));     // Growth AE
const ContentIntel               = lazy(() => import('./pages/marketing/Content'));       // Growth AF
const Geo                        = lazy(() => import('./pages/marketing/Geo'));           // Growth AG
const Brand                      = lazy(() => import('./pages/marketing/Brand'));         // Growth AH
const ContentProduction          = lazy(() => import('./pages/marketing/ContentProduction')); // Growth AI
const CommandCenter              = lazy(() => import('./pages/marketing/CommandCenter'));    // Growth Command Center
const GrowthBrainPage            = lazy(() => import('./pages/marketing/GrowthBrainPage'));  // Growth Brain
const GrowthGoal                 = lazy(() => import('./pages/marketing/GrowthGoal'));       // Growth Goal
const WebFormPage                = lazy(() => import('./pages/public/WebFormPage'));
const WebForms                   = lazy(() => import('./pages/marketing/WebForms'));
const WebFormBuilder             = lazy(() => import('./pages/marketing/WebFormBuilder'));
const SalesReports              = lazy(() => import('./pages/sales/SalesReports'));
const CollectionsReport         = lazy(() => import('./pages/sales/CollectionsReport'));
const LegalDashboard            = lazy(() => import('./pages/legal/LegalDashboard'));
const LegalEscalationDetail     = lazy(() => import('./pages/legal/LegalEscalationDetail'));
const AIWorkforce               = lazy(() => import('./pages/AIWorkforce'));
const TasksPage                 = lazy(() => import('./pages/TasksPage'));
const CompliancePage            = lazy(() => import('./pages/CompliancePage'));

// ── Module loading fallback ───────────────────────────────────────────────────
// Fallback shown while a lazy() chunk is being fetched. Kept minimal —
// no big "Loading module…" banner that reads as "system is broken." A
// thin top progress bar pulses subtly. With webpackPrefetch on
// Processing/HR/lazy chunks, this fallback rarely renders after first
// navigation since chunks are cached.
function PageLoader() {
  return (
    <div className="fixed top-0 left-0 right-0 h-0.5 bg-teal-500 animate-pulse z-50 pointer-events-none" />
  );
}

// ── Wrap each route with isolation + loading state ────────────────────────────
function Module({ name, children }) {
  return (
    <ModuleErrorBoundary moduleName={name}>
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </ModuleErrorBoundary>
  );
}

// Redirect target for "/" and any unknown route. An AUTHENTICATED user is sent
// to THEIR OWN dashboard (never to /login, which clears the session and looks
// like a random logout — the failure mode when a notification/in-app link
// pointed at an undefined or other-role route). Only genuinely logged-out users
// go to /login.
function HomeOrLogin() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={user ? roleHome(user.role) : '/login'} replace />;
}

// Checks on every authenticated load whether legal docs need re-acceptance
function LegalGate({ children }) {
  const { user } = useAuth();
  const [pendingDocs, setPendingDocs] = useState(null); // null = not checked yet

  useEffect(() => {
    // Clients and B2B partners are the TENANT's counterparties, not
    // Euphoric's. These are Euphoric's platform documents — Terms of
    // Service, Privacy Policy, DPA — governing the tenant's use of the OS.
    // A B2B partner has no relationship with Euphoric at all, so being asked
    // to accept them is legally meaningless, and on a white-label tenant it
    // reveals a vendor the partner is never supposed to hear about.
    // Their agreement with the tenant is papered separately (D1: the partner
    // signs one master agreement).
    if (!user || user.role === 'client' || user.role === 'b2b_partner') {
      setPendingDocs([]);
      return;
    }
    legalAPI.checkAcceptanceStatus()
      .then(res => {
        const { needs_reacceptance, documents } = res.data;
        setPendingDocs(needs_reacceptance ? documents : []);
      })
      .catch(() => setPendingDocs([])); // non-fatal — never block on error
  }, [user]);

  // Still checking — render children normally (avoid flash)
  if (pendingDocs === null) return children;

  // W-legal: WHITE-LABEL tenants contract with their PARTNER, not Euphoric.
  // The backend already serves them the partner-terms edition (their
  // acceptance-status returns the partner's document, never Euphoric's) —
  // here we only re-skin the modal chrome with the partner's brand so no
  // Euphoric mark appears around it.
  const wlBrand = (() => {
    try {
      const host = window.location.hostname;
      const cached = localStorage.getItem(`branding_${host.split('.')[0]}`);
      if (cached) {
        const wl = JSON.parse(cached).white_label;
        return wl ? { platform_name: wl.platform_name || null, logo_url: wl.logo_url || null } : null;
      }
      // No cached branding yet — infer only from an unambiguous partner host
      // (3+ labels, not our platform, not a Vercel preview, not local dev).
      const inferred = host.split('.').length >= 3
        && !host.endsWith('.euphoricai.io')
        && !host.endsWith('.vercel.app');
      return inferred ? { platform_name: null, logo_url: null } : null;
    } catch { return null; }
  })();

  return (
    <>
      {children}
      {pendingDocs.length > 0 && (
        <LegalReacceptanceModal
          documents={pendingDocs}
          brand={wlBrand}
          onAccepted={() => setPendingDocs([])}
        />
      )}
    </>
  );
}

function App() {
  return (
    <BrandingProvider>
    <AuthProvider>
    {/* Roles & Access — inside AuthProvider (it needs the signed-in user),
        outside the router so the grid is fetched once per session, not once
        per navigation. */}
    <PermissionsProvider>
      <BrowserRouter>
        <div className="app-container">
          <ErrorBoundary>
          <LegalGate>
          <Routes>
            {/* Legal Certificate — public route, no auth required.
                Serves the branded consent certificate at {slug}.euphoricai.io/legal/certificate/:id
                so customers NEVER see the raw onrender.com backend URL in their emails. */}
            <Route path="/legal/certificate/:id" element={<LegalCertificatePage />} />

            {/* Web form — public route, no auth. This is the page a visitor
                fills in on the tenant's own website (embed.js frames it) or
                opens from a shared link. It must stay outside every guard:
                the visitor has no account and no tenant session. */}
            <Route path="/f/:key" element={<WebFormPage />} />

            {/* Auth Routes — no lazy loading needed, always accessible */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Profile Routes */}
            <Route path="/profile" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'case_officer', 'client', 'accountant', 'sales_consultant', 'sales_manager', 'marketing_manager']}>
                <Module name="Profile"><ProfilePage /></Module>
              </ProtectedRoute>
            } />
            <Route path="/change-password" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'case_officer', 'client', 'accountant', 'sales_consultant', 'sales_manager', 'marketing_manager', 'b2b_partner']}>
                <Module name="Change Password"><ChangePasswordPage /></Module>
              </ProtectedRoute>
            } />
            
            {/* Admin Routes (Super Admin & Process Manager) */}
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="Admin Dashboard"><AdminDashboard /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/staff" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="Admin Staff"><AdminStaff /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/clients" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="Admin Clients"><AdminClients /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/cases" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="Admin Cases"><AdminCases /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/complaints" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="Complaints"><AdminComplaints /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/processing/overview" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="Processing Overview"><ProcessingOverview /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/pipeline" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="Case Pipeline"><CasePipeline /></Module>
              </ProtectedRoute>
            } />
            {/* Autopilot — every staff role sees THEIR scoped briefing (backend
                view gate + ask_policy scoping decide what: an owner gets firm
                health, a consultant gets "Your focus" over the work they own).
                Scan/Standards stay manager/admin inside the page. Mirrors the
                /ask role list — same exclusions (client, affiliate). */}
            <Route path="/admin/autopilot" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager',
                'sales_manager', 'sales_consultant', 'case_officer', 'accountant',
                'marketing_manager', 'legal_counsel']}>
                <Module name="Autopilot"><Autopilot /></Module>
              </ProtectedRoute>
            } />
            {/* Ask Euphoric — every staff role; role policy on the backend
                decides what each may ask (client/affiliate excluded there too) */}
            <Route path="/ask" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager',
                'sales_manager', 'sales_consultant', 'case_officer', 'accountant',
                'marketing_manager', 'legal_counsel']}>
                <Module name="Ask Euphoric"><AskEuphoric /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/recycle-bin" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                <Module name="Recycle Bin"><RecycleBinPage /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/leads/deletion-report" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <Module name="Lead Deletion Report"><LeadDeletionReportPage /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/leads/reassignment-report" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'sales_manager']}>
                <Module name="Lead Reassignment Report"><LeadReassignmentReportPage /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/discount/policy" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                <Module name="Discount Policy"><DiscountPolicyPage /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/discount/approvals" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'sales_manager', 'marketing_manager', 'process_manager', 'accountant', 'legal_counsel']}>
                <Module name="Discount Approvals"><DiscountApprovalsPage /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/discount/audit" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                <Module name="Discount Audit"><DiscountAuditPage /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/visa-programs" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                <Module name="Admin Visa Programs"><AdminVisaPrograms /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/b2b-partners/register-client" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'accountant']}>
                <Module name="B2B Register Client"><B2BRegisterClient /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/b2b-partners" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="B2 B Partners"><B2BPartners /></Module>
              </ProtectedRoute>
            } />
            {/* Checklist templates are catalog config — {super_admin, admin}
                only, matching POST /checklist-templates. process_manager dropped
                (it saw the page but silent-403'd on every write). */}
            <Route path="/admin/checklists" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                <Module name="Admin Checklist Templates"><AdminChecklistTemplates /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/analytics" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="Admin Dashboard"><AdminDashboard /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="Users Management"><UsersManagement /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/hr/overview" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'accountant']}>
                <Module name="HR Overview"><HROverview /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/hr/attendance" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'accountant', 'sales_manager']}>
                <Module name="Attendance Ledger"><AttendanceLedger /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/hr/payroll" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'accountant']}>
                <Module name="Payroll Month-End"><PayrollMonthEnd /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/hr/policy" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="HR Policy"><HRPolicySettings /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/hr/job-applications" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="Job Applications"><JobApplications /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/hr/leaves" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'accountant', 'sales_manager', 'sales_consultant', 'case_officer', 'marketing_manager', 'legal_counsel']}>
                <Module name="Leaves"><Leaves /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/hr/my-attendance" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'accountant', 'sales_manager', 'sales_consultant', 'case_officer', 'marketing_manager', 'legal_counsel']}>
                <Module name="MyAttendance"><MyAttendance /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/settings" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="Settings Page"><SettingsPage /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/lead-intake-settings" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                <Module name="Lead Intake Settings"><LeadIntakeSettings /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/email-logs" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="Email Logs"><EmailLogs /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/email-templates" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="Email Templates"><EmailTemplates /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/document-library" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="Document Library"><DocumentLibrary /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/email" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="Email Module"><EmailModule /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/complaint-types" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="Complaint Natures Management"><ComplaintNaturesManagement /></Module>
              </ProtectedRoute>
            } />
            
            {/* Accounting Routes (Super Admin, Admin & Accountant) */}
            <Route path="/admin/accounting" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'accountant']}>
                <Module name="Accounting Dashboard"><AccountingDashboard /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/accounting/sales" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'accountant']}>
                <Module name="Sales Management"><SalesManagement /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/accounting/expenses" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'accountant']}>
                <Module name="Expense Management"><ExpenseManagement /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/accounting/collections" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'accountant']}>
                <Module name="Collections"><CollectionsQueue /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/accounting/reports" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'accountant']}>
                <Module name="Financial Reports"><FinancialReports /></Module>
              </ProtectedRoute>
            } />
            
            {/* Accountant Routes */}
            <Route path="/accountant" element={
              <ProtectedRoute allowedRoles={['accountant']}>
                <Module name="Accounting Dashboard"><AccountingDashboard /></Module>
              </ProtectedRoute>
            } />
            <Route path="/accountant/accounting" element={
              <ProtectedRoute allowedRoles={['accountant']}>
                <Module name="Accounting Dashboard"><AccountingDashboard /></Module>
              </ProtectedRoute>
            } />
            <Route path="/accountant/accounting/sales" element={
              <ProtectedRoute allowedRoles={['accountant']}>
                <Module name="Sales Management"><SalesManagement /></Module>
              </ProtectedRoute>
            } />
            <Route path="/accountant/accounting/collections" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'accountant']}>
                <Module name="Collections"><CollectionsQueue /></Module>
              </ProtectedRoute>
            } />
            <Route path="/accountant/accounting/expenses" element={
              <ProtectedRoute allowedRoles={['accountant']}>
                <Module name="Expense Management"><ExpenseManagement /></Module>
              </ProtectedRoute>
            } />
            <Route path="/accountant/accounting/reports" element={
              <ProtectedRoute allowedRoles={['accountant']}>
                <Module name="Financial Reports"><FinancialReports /></Module>
              </ProtectedRoute>
            } />
            <Route path="/accountant/email" element={
              <ProtectedRoute allowedRoles={['accountant']}>
                <Module name="Email Module"><EmailModule /></Module>
              </ProtectedRoute>
            } />
            
            {/* Process Manager Routes */}
            <Route path="/manager" element={
              <ProtectedRoute allowedRoles={['admin', 'process_manager']}>
                <Module name="Admin Dashboard"><AdminDashboard /></Module>
              </ProtectedRoute>
            } />
            <Route path="/manager/cases" element={
              <ProtectedRoute allowedRoles={['admin', 'process_manager']}>
                <Module name="Admin Cases"><AdminCases /></Module>
              </ProtectedRoute>
            } />
            <Route path="/manager/pipeline" element={
              <ProtectedRoute allowedRoles={['admin', 'process_manager']}>
                <Module name="Case Pipeline"><CasePipeline /></Module>
              </ProtectedRoute>
            } />
            <Route path="/manager/clients" element={
              <ProtectedRoute allowedRoles={['admin', 'process_manager']}>
                <Module name="Admin Clients"><AdminClients /></Module>
              </ProtectedRoute>
            } />
            <Route path="/manager/officers" element={
              <ProtectedRoute allowedRoles={['admin', 'process_manager']}>
                <Module name="Admin Staff"><AdminStaff /></Module>
              </ProtectedRoute>
            } />
            <Route path="/manager/performance" element={
              <ProtectedRoute allowedRoles={['admin', 'process_manager']}>
                <Module name="Admin Dashboard"><AdminDashboard /></Module>
              </ProtectedRoute>
            } />
            <Route path="/manager/users" element={
              <ProtectedRoute allowedRoles={['admin', 'process_manager']}>
                <Module name="Users Management"><UsersManagement /></Module>
              </ProtectedRoute>
            } />
            <Route path="/manager/settings" element={
              <ProtectedRoute allowedRoles={['admin', 'process_manager']}>
                <Module name="Settings Page"><SettingsPage /></Module>
              </ProtectedRoute>
            } />
            
            {/* Case Officer Routes */}
            <Route path="/officer" element={
              <ProtectedRoute allowedRoles={['case_officer']}>
                <Module name="Case Officer Dashboard"><CaseOfficerDashboard /></Module>
              </ProtectedRoute>
            } />
            <Route path="/officer/cases" element={
              <ProtectedRoute allowedRoles={['case_officer']}>
                <Module name="Admin Cases"><AdminCases /></Module>
              </ProtectedRoute>
            } />
            <Route path="/officer/pipeline" element={
              <ProtectedRoute allowedRoles={['case_officer']}>
                <Module name="Case Pipeline"><CasePipeline /></Module>
              </ProtectedRoute>
            } />
            <Route path="/officer/cases/:caseId" element={
              <ProtectedRoute allowedRoles={['case_officer', 'super_admin', 'admin', 'process_manager', 'legal_counsel']}>
                <Module name="Case Detail"><CaseDetail /></Module>
              </ProtectedRoute>
            } />
            <Route path="/officer/documents" element={
              <ProtectedRoute allowedRoles={['case_officer']}>
                <Module name="Officer Documents"><OfficerDocuments /></Module>
              </ProtectedRoute>
            } />
            <Route path="/officer/document-library" element={
              <ProtectedRoute allowedRoles={['case_officer']}>
                <Module name="Document Library"><DocumentLibrary /></Module>
              </ProtectedRoute>
            } />
            <Route path="/officer/email" element={
              <ProtectedRoute allowedRoles={['case_officer']}>
                <Module name="Email Module"><EmailModule /></Module>
              </ProtectedRoute>
            } />
            <Route path="/officer/complaints" element={
              <ProtectedRoute allowedRoles={['case_officer']}>
                <Module name="Officer Complaints"><OfficerComplaints /></Module>
              </ProtectedRoute>
            } />
            
            {/* Admin Client Detail Route — sales roles land here from
                LeadDetail/LeadsKanban after lead conversion and see a
                masked status-only view (no passport, no case detail). */}
            <Route path="/admin/clients/:clientId" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'sales_manager', 'sales_consultant']}>
                <Module name="Admin Client Detail"><AdminClientDetail /></Module>
              </ProtectedRoute>
            } />

            {/* Admin Case Detail Route */}
            <Route path="/admin/cases/:caseId" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'legal_counsel']}>
                <Module name="Case Detail"><CaseDetail /></Module>
              </ProtectedRoute>
            } />

            {/* Manager Case Detail Route */}
            <Route path="/manager/cases/:caseId" element={
              <ProtectedRoute allowedRoles={['admin', 'process_manager', 'legal_counsel']}>
                <Module name="Case Detail"><CaseDetail /></Module>
              </ProtectedRoute>
            } />
            
            {/* B2B Partner Portal — a partner is neither staff nor a client.
                Their routes never render the admin shell, and every endpoint
                behind them resolves the partner id server-side. */}
            <Route path="/partner" element={
              <ProtectedRoute allowedRoles={['b2b_partner']}>
                <Module name="Partner Portal"><PartnerToday /></Module>
              </ProtectedRoute>
            } />
            <Route path="/partner/clients" element={
              <ProtectedRoute allowedRoles={['b2b_partner']}>
                <Module name="Partner Portal"><PartnerClients /></Module>
              </ProtectedRoute>
            } />
            <Route path="/partner/cases/:caseId" element={
              <ProtectedRoute allowedRoles={['b2b_partner']}>
                <Module name="Partner Portal"><PartnerCaseDetail /></Module>
              </ProtectedRoute>
            } />

            {/* Client Routes */}
            <Route path="/client" element={
              <ProtectedRoute allowedRoles={['client']}>
                <Module name="Client Portal"><ClientPortal /></Module>
              </ProtectedRoute>
            } />
            <Route path="/client/application" element={
              <ProtectedRoute allowedRoles={['client']}>
                <Module name="Client Portal"><ClientPortal /></Module>
              </ProtectedRoute>
            } />
            <Route path="/client/documents" element={
              <ProtectedRoute allowedRoles={['client']}>
                <Module name="Client Portal"><ClientPortal /></Module>
              </ProtectedRoute>
            } />
            <Route path="/client/complaints" element={
              <ProtectedRoute allowedRoles={['client']}>
                <Module name="Client Complaints"><ClientComplaints /></Module>
              </ProtectedRoute>
            } />
            
            {/* ── Sales Consultant Routes ── */}
            <Route path="/sales" element={
              <ProtectedRoute allowedRoles={['sales_consultant', 'sales_manager', 'super_admin', 'admin', 'process_manager']}>
                <Module name="Sales Dashboard"><SalesDashboard /></Module>
              </ProtectedRoute>
            } />
            <Route path="/sales/leads" element={
              <ProtectedRoute allowedRoles={['sales_consultant', 'sales_manager', 'super_admin', 'admin', 'process_manager']}>
                <Module name="Leads Kanban"><LeadsKanban /></Module>
              </ProtectedRoute>
            } />
            <Route path="/sales/lead-pool" element={
              <ProtectedRoute allowedRoles={['sales_consultant', 'sales_manager', 'super_admin', 'admin', 'marketing_manager']}>
                <Module name="Lead Pool"><LeadPoolPage /></Module>
              </ProtectedRoute>
            } />
            <Route path="/sales/leads/:leadId" element={
              <ProtectedRoute allowedRoles={['sales_consultant', 'sales_manager', 'super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <Module name="Lead Detail"><LeadDetail /></Module>
              </ProtectedRoute>
            } />
            <Route path="/sales/email" element={
              <ProtectedRoute allowedRoles={['sales_consultant', 'sales_manager']}>
                <Module name="Email Module"><EmailModule /></Module>
              </ProtectedRoute>
            } />
            <Route path="/sales/attendance" element={
              <ProtectedRoute allowedRoles={['sales_consultant', 'sales_manager', 'super_admin', 'admin', 'process_manager']}>
                <Module name="Sales Dashboard"><SalesDashboard /></Module>
              </ProtectedRoute>
            } />

            {/* ── Sales Reports — role-scoped: consultants see only their own data ── */}
            <Route path="/sales/reports" element={
              <ProtectedRoute allowedRoles={['sales_consultant', 'sales_manager', 'super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <Module name="Sales Reports"><SalesReports /></Module>
              </ProtectedRoute>
            } />

            {/* ── Collections — cash received by payment month (the incentive
                   lens). Roles = exactly the backend's ALLOWED_ROLES for
                   /sales/reports/collections; wider frontend access would
                   403 into an empty page. ── */}
            <Route path="/sales/collections" element={
              <ProtectedRoute allowedRoles={['sales_consultant', 'sales_manager', 'super_admin', 'admin']}>
                <Module name="Sales Reports"><CollectionsReport /></Module>
              </ProtectedRoute>
            } />

            {/* ── Sales Manager Routes ── */}
            <Route path="/sales/manager" element={
              <ProtectedRoute allowedRoles={['sales_manager', 'super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <Module name="Sales Manager Dashboard"><SalesManagerDashboard /></Module>
              </ProtectedRoute>
            } />
            <Route path="/sales/manager/leads" element={
              <ProtectedRoute allowedRoles={['sales_manager', 'super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <Module name="Leads Kanban"><LeadsKanban /></Module>
              </ProtectedRoute>
            } />
            <Route path="/sales/manager/leaderboard" element={
              <ProtectedRoute allowedRoles={['sales_manager', 'super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <Module name="Sales Manager Dashboard"><SalesManagerDashboard /></Module>
              </ProtectedRoute>
            } />
            <Route path="/sales/manager/attendance" element={
              <ProtectedRoute allowedRoles={['sales_manager', 'super_admin', 'admin', 'process_manager']}>
                <Module name="Sales Manager Dashboard"><SalesManagerDashboard /></Module>
              </ProtectedRoute>
            } />

            {/* ── Marketing Module Routes ── */}
            <Route path="/admin/marketing" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <Module name="Marketing Dashboard"><MarketingDashboard /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing/upload" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <Module name="Marketing Leads Upload"><MarketingLeadsUpload /></Module>
              </ProtectedRoute>
            } />
            {/* Phase 1.10b — Meta Ads performance. Allow-list matches the
                backend MARKETING_ROLES gate on /marketing/meta-performance. */}
            <Route path="/admin/marketing/meta" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <Module name="Meta Ads Performance"><MetaPerformance /></Module>
              </ProtectedRoute>
            } />
            {/* Acquisition Intelligence Phase H — allow-list matches the backend
                ACQUISITION_ROLES (== MARKETING_ROLES) gate on /marketing/acquisition/*. */}
            <Route path="/admin/marketing/acquisition" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <Module name="Acquisition"><Acquisition /></Module>
              </ProtectedRoute>
            } />
            {/* Growth AA — Technical SEO. GrowthEngineRoute keeps it invisible to tenants the API would 403. */}
            <Route path="/admin/marketing/technical-seo" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <GrowthEngineRoute><Module name="Technical SEO"><TechnicalSeo /></Module></GrowthEngineRoute>
              </ProtectedRoute>
            } />
            {/* Growth AB — Keyword Intelligence. Same gate: hidden from tenants the API would 403. */}
            <Route path="/admin/marketing/keywords" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <GrowthEngineRoute><Module name="Keyword Intelligence"><Keywords /></Module></GrowthEngineRoute>
              </ProtectedRoute>
            } />
            {/* Growth AC — Competitor Intelligence. Same gate: hidden from tenants the API would 403. */}
            <Route path="/admin/marketing/competitors" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <GrowthEngineRoute><Module name="Competitor Intelligence"><Competitors /></Module></GrowthEngineRoute>
              </ProtectedRoute>
            } />
            {/* Growth AD — Gap Intelligence. Same gate: hidden from tenants the API would 403. */}
            <Route path="/admin/marketing/gaps" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <GrowthEngineRoute><Module name="Gap Intelligence"><Gaps /></Module></GrowthEngineRoute>
              </ProtectedRoute>
            } />
            {/* Growth AE — Authority Intelligence. Same gate: hidden from tenants the API would 403. */}
            <Route path="/admin/marketing/authority" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <GrowthEngineRoute><Module name="Authority Intelligence"><Authority /></Module></GrowthEngineRoute>
              </ProtectedRoute>
            } />
            {/* Growth AF — Content Intelligence. Same gate: hidden from tenants the API would 403. */}
            <Route path="/admin/marketing/content" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <GrowthEngineRoute><Module name="Content Intelligence"><ContentIntel /></Module></GrowthEngineRoute>
              </ProtectedRoute>
            } />
            {/* Growth AG — GEO & AI Visibility. Same gate: hidden from tenants the API would 403. */}
            <Route path="/admin/marketing/geo" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <GrowthEngineRoute><Module name="AI Visibility"><Geo /></Module></GrowthEngineRoute>
              </ProtectedRoute>
            } />
            {/* Growth AH — Brand Intelligence. Same gate: hidden from tenants the API would 403. */}
            <Route path="/admin/marketing/brand" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <GrowthEngineRoute><Module name="Brand Intelligence"><Brand /></Module></GrowthEngineRoute>
              </ProtectedRoute>
            } />
            {/* Growth AI — Content Production (execution). Same gate; the item view is the same page.
                Reads stay open to the Growth read roles; every transition is admin-gated in the API. */}
            {/* ── Growth Command Center — the primary Growth home. Same gate as every Growth page:
                hidden from tenants the API would 403, and every state change is admin-gated server-side. */}
            <Route path="/admin/marketing/growth" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <GrowthEngineRoute><Module name="Growth Command Center"><CommandCenter /></Module></GrowthEngineRoute>
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing/brain" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <GrowthEngineRoute><Module name="Growth Brain"><GrowthBrainPage /></Module></GrowthEngineRoute>
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing/goal" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <GrowthEngineRoute><Module name="Growth Goal"><GrowthGoal /></Module></GrowthEngineRoute>
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing/content-production" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <GrowthEngineRoute><Module name="Content Production"><ContentProduction /></Module></GrowthEngineRoute>
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing/content-production/:itemId" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <GrowthEngineRoute><Module name="Content Production"><ContentProduction /></Module></GrowthEngineRoute>
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing/history" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <Module name="Marketing Import History"><MarketingImportHistory /></Module>
              </ProtectedRoute>
            } />
            {/* Marketing-led GTM (HubSpot model): Marketing OWNS the unassigned
                pool. Sales managers redistribute from their team's pipeline,
                not the marketing inbox. Process / Legal carved out by
                department-scoping. The ProtectedRoute allow-list MUST stay a
                subset of get_lead_pool's allow-list in
                backend/routers/marketing.py — otherwise authorized users
                hit 403 and see an empty page. */}
            <Route path="/admin/marketing/pool" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'marketing_manager']}>
                <Module name="Lead Pool"><LeadPool /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing/analytics" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <Module name="Marketing Dashboard"><MarketingDashboard /></Module>
              </ProtectedRoute>
            } />
            {/* Assigned-Leads report. allowedRoles MUST stay a subset of
                reports_assigned_leads' ASSIGNED_REPORT_ROLES in
                backend/routers/sales_reports.py — else an authorized-by-route
                user hits a silent 403 and sees an empty page. */}
            <Route path="/admin/marketing/assigned-leads" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'marketing_manager', 'sales_manager']}>
                <Module name="Assigned Leads Report"><AssignedLeadsReport /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing/sources" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'marketing_manager']}>
                <Module name="Lead Sources"><LeadSources /></Module>
              </ProtectedRoute>
            } />

            {/* Web Forms — build a form, paste it on your own website, leads
                arrive in the Lead Pool. Same allow-list as the Lead Pool and
                Lead Sources (backend /web-forms: marketing_manager + admins),
                so nobody reaches a page whose data they cannot load. */}
            <Route path="/admin/marketing/web-forms" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'marketing_manager']}>
                <Module name="Web Forms"><WebForms /></Module>
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing/web-forms/:formId" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'marketing_manager']}>
                <Module name="Web Form Builder"><WebFormBuilder /></Module>
              </ProtectedRoute>
            } />

            {/* Legal Counsel Routes */}
            <Route path="/legal" element={
              <ProtectedRoute allowedRoles={['legal_counsel', 'super_admin', 'admin', 'process_manager']}>
                <Module name="Legal Dashboard"><LegalDashboard /></Module>
              </ProtectedRoute>
            } />
            <Route path="/legal/escalations" element={
              <ProtectedRoute allowedRoles={['legal_counsel', 'super_admin', 'admin', 'process_manager']}>
                <Module name="Legal Dashboard"><LegalDashboard /></Module>
              </ProtectedRoute>
            } />
            <Route path="/legal/escalations/:id" element={
              <ProtectedRoute allowedRoles={['legal_counsel', 'super_admin', 'admin', 'process_manager', 'case_officer', 'sales_consultant', 'client']}>
                <Module name="Legal Escalation Detail"><LegalEscalationDetail /></Module>
              </ProtectedRoute>
            } />
            <Route path="/legal/analytics" element={
              <ProtectedRoute allowedRoles={['legal_counsel', 'super_admin', 'admin', 'process_manager']}>
                <Module name="Legal Dashboard"><LegalDashboard /></Module>
              </ProtectedRoute>
            } />

            {/* Compliance Center */}
            <Route path="/compliance" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'legal_counsel']}>
                <Module name="Compliance Page"><CompliancePage /></Module>
              </ProtectedRoute>
            } />

            {/* Tasks */}
            <Route path="/tasks" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'case_officer', 'sales_manager', 'sales_consultant', 'accountant']}>
                <Module name="Tasks Page"><TasksPage /></Module>
              </ProtectedRoute>
            } />

            {/* AI Workforce */}
            <Route path="/ai-workforce" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <Module name="A I Workforce"><AIWorkforce /></Module>
              </ProtectedRoute>
            } />

            {/* Notifications — add new roles */}
            <Route path="/notifications" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'case_officer', 'client', 'accountant', 'sales_consultant', 'sales_manager', 'marketing_manager', 'legal_counsel']}>
                <Module name="Notifications Page"><NotificationsPage /></Module>
              </ProtectedRoute>
            } />

            {/* Default redirect — authenticated users go to their own dashboard,
                never to /login (which would clear the session). */}
            <Route path="/" element={<HomeOrLogin />} />
            <Route path="*" element={<HomeOrLogin />} />
          </Routes>
          </LegalGate>
          {/* The Toaster wrapper (ui/sonner) carries its own ToastCrashGuard:
              a stray non-string toast payload (React error #31 — object
              rendered as a child) kills only the toaster, never this tree.
              Call sites additionally coerce structured error details through
              utils/apiError so the message still reaches the user. */}
          <Toaster position="top-right" />
          <CookieConsent />
          {/* Morning Briefing pop-up — once per day after login, and always
              AFTER clock-in (it waits for attendance; a briefing must never
              delay a punch and cost someone salary). */}
          <MorningBriefingModal />
          {/* ⌘K — Autopilot's chat from every authenticated screen. */}
          <AskAnywherePalette />
          </ErrorBoundary>
        </div>
      </BrowserRouter>
    </PermissionsProvider>
    </AuthProvider>
    </BrandingProvider>
  );
}

export default App;
